<script lang="ts" setup>
  import { ref, type PropType } from 'vue';
  import type { SwiperOptions } from 'swiper/types';

  type Category = {
    id: number;
    title: string;
    slug: string;
    photo: string | null;
    children: Category[];
  }

  type Brand = {
    id: number;
    name: string;
    slug: string;
    photo: string | null;
  }

  defineOptions({
    inheritAttrs: false,
  });

  const props = defineProps({
    tree: { type: Array as PropType<Category[]>, required: true, default: () => [] },
    brands: { type: Array as PropType<Brand[]>, required: false, default: () => [] },
    header: String,
    headerType: String,
    id: Number,
  });

  const openItemId = ref<number | undefined>(props.tree[0]?.id);

  const parameters = ref<SwiperOptions>({
    navigation: true,
    breakpoints: {
      1: {
        slidesPerView: 5.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      576: {
        slidesPerView: 6.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      768: {
        slidesPerView: 7.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
      },
      992: {
        slidesPerView: 10,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
      1200: {
        slidesPerView: 12,
        spaceBetween: 16,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
      },
    },
  });

  const parametersCategories = ref<SwiperOptions>({
    speed: 300,
    spaceBetween: 10,
    watchOverflow: true,
    slidesPerView: 4,
    grid: {
      rows: 1,
      fill: 'column',
    },
    breakpoints: {
      1: {
        slidesPerView: 2.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
        grid: {
          rows: 1,
          fill: 'column',
        },
      },
      576: {
        slidesPerView: 3.5,
        spaceBetween: 10,
        slidesOffsetBefore: 10,
        slidesOffsetAfter: 10,
        grid: {
          rows: 1,
          fill: 'column',
        },
      },
      993: {
        slidesPerView: 4,
        spaceBetween: 10,
        slidesOffsetBefore: 0,
        slidesOffsetAfter: 0,
        grid: {
          rows: 2,
          fill: 'column',
        },
      },
    },
  });

  function setOpenItem(id?: number) {
    if (id !== undefined) {
      openItemId.value = id;
    }
  }
</script>
<template>
  <SfSection v-if="tree.length > 0" :title="header" :headerType class="dynamic-categories">
    <div class="dynamic-categories__content">
      <template v-if="tree.length">
        <div class="dynamic-categories__top" @mouseleave="setOpenItem(tree[0]?.id)">
          <div class="dynamic-categories__list custom-scrollbar">
            <DynamicCategoriesListItem
              v-for="node in tree"
              :key="node.id"
              v-model:openItemId="openItemId"
              :node="node"
              :open="openItemId === node.id"
              :aria-controls="`${id}-dynamic-category-${node.id}`"
            />
          </div>
          <template v-for="node in tree">
            <div v-show="openItemId === node.id">
              <CategoriesCarousel
                v-if="node.children.length > 0"
                :parameters="parametersCategories"
                :categories="node.children"
                :id="`${id}-dynamic-category-${node.id}`"
                class="dynamic-categories__categories-carousel"
              />
              <SfDataPlaceholder v-else>
                {{ $t('SHOP.COMMON.MESSAGE.NO_CATEGORIES_TO_SHOW') }}
              </SfDataPlaceholder>
            </div>
          </template>
        </div>
      </template>
      <SfBrandsCarousel v-if="brands?.length" :brands :parameters class="dynamic-categories__brands-carousel" />
    </div>
  </SfSection>
</template>
<style lang="scss" scoped>
  .dynamic-categories {
    &__top {
      display: grid;
      grid-template-columns: 300px 1fr;
      gap: 1rem;
      overflow: hidden;
    }

    &__categories-carousel {
      :deep(.swiper-wrapper) {
        display: flex;
        flex-direction: column;
        align-content: flex-start;
        flex-wrap: wrap;
        height: 300px;
        width: auto !important;

        .swiper-slide {
          height: 140px;
          justify-content: center;
          border-radius: var(--border-radius);

          .categories-carousel__link {
            gap: 0.5rem;
            padding: 0.25rem;
            justify-content: space-evenly;

            img {
              height: 60px;
            }

            .categories-carousel__category-title {
              -webkit-line-clamp: 2;
              font-size: 0.75rem;
            }
          }
        }
      }
    }

    &__content {
      position: relative;
      display: flex;
      flex-direction: column;
      overflow: hidden;
      gap: 1rem;
    }

    &__brands-carousel :deep(.items-carousel__slide) {
      border-radius: 0;
    }

    &__list {
      list-style: none;
      background: var(--color-light-background, #EEEEEE);
      max-width: 310px;
      border-radius: var(--border-radius);
      padding: calc(var(--border-radius) + 0.5rem) 0 calc(var(--border-radius) + 0.5rem) 1rem;
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      font-size: 0.875rem;
      height: 300px;
      overflow-y: auto;
    }

    &__name {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    @media (max-width: 992px) {
      &__top {
        display: flex;
        flex-direction: column;
      }

      &__content {
        gap: 0.75rem;
      }

      &__list {
        max-width: 100%;
        flex-direction: row;
        overflow: auto;
        height: auto;
        padding: 0.75rem 0.625rem 0 0.625rem;
        border-radius: 0;

        &::-webkit-scrollbar {
          width: 0;
          height: 0;
        }
      }

      &__categories-carousel {
        :deep(.swiper-wrapper) {
          height: 120px;
          flex-flow: unset;
          align-content: unset;
          justify-content: start;

          .swiper-slide {
            height: 120px;
            margin-bottom: 0;
            border-color: var(--primary-color);

            .categories-carousel__link {
              padding: 0.5rem;

              .categories-carousel__category-title {
                font-size: 0.75rem;
              }
            }
          }
        }
      }
    }
  }
</style>
